import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
} from '@angular/core';
import { BaseFormComponent } from '../../core/components/base-form.component';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Beneficjent } from '../../shared/models/beneficjent';
import { Router } from '@angular/router';
import { UserApi } from '../../shared/services/api/user.api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { Card } from 'primeng/card';
import { FormBeneficiaryDetailsComponent } from '../../shared/components/beneficiary/form-beneficiary-details/form-beneficiary-details.component';
import { FormBeneficiaryContactDetailsComponent } from '../../shared/components/beneficiary/form-contact-details/form-beneficiary-contact-details.component';
import { FormBankAccountComponent } from '../../shared/components/bank-account-number/form-bank-account/form-bank-account.component';
import { catchError, finalize, firstValueFrom, tap, throwError } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Button } from 'primeng/button';
import { FormsUtils } from '../../shared/utils/forms.utils';
import { MessageService } from 'primeng/api';
import { SpinnerService } from '../../core/services/spinner.service';
import { ToastSeverity } from '../../shared/enums/toast-severity.enum';
import { APP_PATHS } from '../../core/config/app-paths';
import { FormEditBeneficiaryAddressComponent } from '../edit-beneficiary/components/form-edit-beneficiary-address/form-edit-beneficiary-address.component';
import { EditBeneficiaryFormModel } from '../edit-beneficiary/form-group/edit-beneficiary-form.model';
import { EditBeneficiaryFormBuilder } from '../edit-beneficiary/form-group/edit-beneficiary.form.builder';
import { Checkbox } from 'primeng/checkbox';
import { ControlErrorDirective } from '../../shared/directive/control-error.directive';
import { DefinicjeParametrow } from '../../shared/enums/params-definition.enum';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InfoDialog } from '../../shared/components/info-dialog/info-dialog';
import { DokumentApi } from '../../shared/services/api/dokument.api';

@Component({
  selector: 'app-beneficiary-user',
  imports: [
    ReactiveFormsModule,
    AutoCompleteModule,
    Card,
    FormBeneficiaryDetailsComponent,
    FormBeneficiaryContactDetailsComponent,
    FormBankAccountComponent,
    Button,
    FormEditBeneficiaryAddressComponent,
    Checkbox,
    ControlErrorDirective,
  ],
  templateUrl: './first-login.component.html',
  styleUrl: './first-login.component.scss',
  providers: [UserApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FirstLoginComponent extends BaseFormComponent<EditBeneficiaryFormModel> {
  readonly #destroyRef = inject(DestroyRef);
  readonly #userApi = inject(UserApi);
  readonly #messageService = inject(MessageService);
  readonly #spinnerService = inject(SpinnerService);
  readonly #dialogService = inject(DialogService);
  readonly #dokumentApi = inject(DokumentApi);
  readonly #router = inject(Router);
  protected beneficiary = input<Beneficjent>();

  protected override createFormGroup(): FormGroup<EditBeneficiaryFormModel> {
    const fg = EditBeneficiaryFormBuilder.createFormGroup(this.beneficiary()!);
    fg.controls.akceptacjaRegulaminu.addValidators(Validators.requiredTrue);
    fg.controls.akceptacjaRODO.addValidators(Validators.requiredTrue);
    this.#subscribeToAddressFgControls(fg);
    this.#handleAvailabilityOfForeignBankAccount(fg);

    this.#subscribeToIsBankAccountControl(fg);
    EditBeneficiaryFormBuilder.setAvailabilityOfBankAccount(fg, true);

    return fg;
  }

  #subscribeToAddressFgControls(
    formGroup: FormGroup<EditBeneficiaryFormModel>,
  ): void {
    formGroup.controls.address.controls.adres.controls.czyAdresZagraniczny.valueChanges
      .pipe(
        tap(() =>
          this.#handleAvailabilityOfForeignBankAccount(this.formGroup()),
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #handleAvailabilityOfForeignBankAccount(
    formGroup: FormGroup<EditBeneficiaryFormModel>,
  ): void {
    const czyAdresZagraniczny =
      formGroup.controls.address.controls.adres.controls.czyAdresZagraniczny
        .value;
    if (czyAdresZagraniczny) {
      formGroup.controls.kontoBankowe.controls.isForeign.enable();
    } else {
      formGroup.controls.kontoBankowe.controls.isForeign.setValue(false);
      formGroup.controls.kontoBankowe.controls.isForeign.disable();
    }
  }

  #subscribeToIsBankAccountControl(
    formGroup: FormGroup<EditBeneficiaryFormModel>,
  ): void {
    formGroup?.controls.kontoBankowe.controls.isBankAccount.valueChanges
      .pipe(
        tap(() => {
          EditBeneficiaryFormBuilder.setAvailabilityOfBankAccount(
            formGroup,
            false,
          );
          if (!formGroup?.controls.kontoBankowe.controls.isBankAccount.value) {
            this.#resetBankAccountData(formGroup);
          }
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #resetBankAccountData(formGroup: FormGroup<EditBeneficiaryFormModel>): void {
    formGroup?.controls.kontoBankowe.controls.isForeign.reset();
    formGroup?.controls.kontoBankowe.controls.domesticAccount.reset();
    formGroup?.controls.kontoBankowe.controls.foreignAccount.reset();
  }

  protected save(): void {
    if (!FormsUtils.isReadyForSave(this.formGroup())) {
      this.#messageService.add({
        severity: ToastSeverity.ERROR,
        summary: 'Wystąpił błąd podczas walidacji formularza',
      });
      return;
    }
    this.#spinnerService.show();
    this.#userApi
      .createUser$(EditBeneficiaryFormBuilder.toModel(this.formGroup()))
      .pipe(
        tap(() => this.#handleSaveSuccess()),
        catchError((err) => {
          this.#handleSaveError(err);
          return throwError(() => err);
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.#spinnerService.hide();
        }),
      )
      .subscribe();
  }

  protected decline() {
    this.#router.navigate([APP_PATHS.auth, APP_PATHS.auth.loginMethod]);
  }

  #handleSaveSuccess(): void {
    this.#dialogService.open(InfoDialog, {
      closable: true,
      showHeader: false,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      width: '40vw',
      inputValues: {
        ['header']: 'firstLogin.create.sucess.header',
        ['content']: 'firstLogin.create.sucess.content',
        ['hideCancelButton']: true,
      },
      breakpoints: {
        '960px': '40vw',
        '640px': '90vw',
      },
    });
    this.#router.navigate([APP_PATHS.auth, APP_PATHS.auth.loginMethod]);
  }

  #handleSaveError(error: any): void {
    this.#messageService.add({
      severity: ToastSeverity.ERROR,
      summary: error?.error?.message
        ? error?.error?.message
        : 'Wystąpił błąd podczas zapisu konta użytkownika',
    });
  }

  protected onRegulaminClick(): void {
    this.#spinnerService.show();

    this.#fetchParamDefinition(
      DefinicjeParametrow.STRONA_POLITYKA_PRYWATNOSCI,
    ).then((popupInnerHtml) => {
      this.#openInfoDialog('Polityka prywatności', popupInnerHtml);
    });
  }

  #openInfoDialog(header: string, content: string): DynamicDialogRef {
    return this.#dialogService.open(InfoDialog, {
      closable: true,
      showHeader: false,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      width: '90vw',
      inputValues: {
        ['header']: header,
        ['content']: content,
        ['hideCancelButton']: true,
      },
      breakpoints: {
        '960px': '90vw',
        '640px': '90vw',
      },
    })!;
  }

  #fetchParamDefinition(param: DefinicjeParametrow): Promise<string> {
    return firstValueFrom(this.#dokumentApi.getParamContent$(param))
      .then((resp) => {
        this.#spinnerService.hide();
        return resp;
      })
      .catch((_) => {
        this.#spinnerService.hide();
        return 'Błąd';
      });
  }
}
